import React, { useState, useEffect, useRef } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/common/SEO';
import { PAGE_SEO, getCanonicalUrl } from '../utils/seoConstants';
import StructuredData, { generateOrganizationSchema, generateLocalBusinessSchema } from '../components/common/StructuredData';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  CardActions, 
  Rating, 
  Chip, 
  Divider, 
  CircularProgress, 
  Paper, 
  Alert, 
  IconButton,
  Avatar,
  Stack,
  useTheme,
  useMediaQuery,
  Badge
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import TyumenMapIcon from '../components/icons/TyumenMapIcon';
// Удаляем импорт Unstable_Grid2, так как он не используется
// и может вызывать ошибку: Uncaught TypeError: undefined is not a function
import { motion } from 'framer-motion';

// Добавляем логи для отладки
console.log('Загрузка компонентов в HomePage.jsx');
// Import layout context
import { useThemeLayout } from '../contexts/ThemeLayoutContext';
// Import layouts
import { layoutMap } from '../layouts/homeLayouts';
import ImageSlideshow from '../components/common/ImageSlideshow';
import LogoSlideshow from '../components/common/LogoSlideshow';
import ContactButtons from '../components/common/ContactButtons';
// Import seasonal hero component
import SeasonalHero from '../components/hero/SeasonalHero';
// Import seasonal effects CSS
import '../components/hero/seasonalEffects.css';
// Import email verification component

// Import auth store
import { useAuthStore } from '../store/authStore';
// Import services component
import FeaturedServices from '../components/home/FeaturedServices';
// Import tips component
import FeaturedTips from '../components/home/FeaturedTips';
// Import telegram bot banner
import TelegramBotBanner from '../components/common/TelegramBotBanner';
// Import promotion banner component
import PromotionBanner from '../components/home/PromotionBanner';

// Hero section with animation and slideshow
const Hero = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { user: userInfo } = useAuthStore();
  
  const [slideshowImages, setSlideshowImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seasonalMessage, setSeasonalMessage] = useState('');
  const [logoExists, setLogoExists] = useState(true);
  const [activeCoupons, setActiveCoupons] = useState(0);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const logoPath = '/assets/logo/logo.png';
  const logoUrl = `${logoPath}?v=${new Date().getTime()}`;
  
  // Массив сообщений для чередования
  const [messages, setMessages] = useState([
    'Русские бани в Тюмени',
    'Загородные дома',
    'Бани на дровах',
    'Места для праздников',
    'Акция 5+1'
  ]);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  
  // Проверяем существование логотипа
  useEffect(() => {
    const checkLogoExists = () => {
      const img = new Image();
      img.onload = () => setLogoExists(true);
      img.onerror = () => setLogoExists(false);
      img.src = logoPath;
    };
    
    checkLogoExists();
    
    // Слушаем событие обновления логотипа
    const handleLogoUpdate = () => {
      checkLogoExists();
    };
    
    window.addEventListener('logoUpdated', handleLogoUpdate);
    
    return () => {
      window.removeEventListener('logoUpdated', handleLogoUpdate);
    };
  }, []);
  
  // Handle theme message updates from SeasonalHero
  const handleThemeChange = (message) => {
    setSeasonalMessage(message);
    // Добавляем сезонное сообщение в массив сообщений, если оно есть и его еще нет в массиве
    if (message && message.trim() !== '') {
      setMessages(prevMessages => {
        if (!prevMessages.includes(message)) {
          return [...prevMessages, message];
        }
        return prevMessages;
      });
    }
  };
  
  // Эффект для чередования сообщений
  useEffect(() => {
    const messageInterval = setInterval(() => {
      setCurrentMessageIndex(prevIndex => (prevIndex + 1) % messages.length);
    }, 4000); // Меняем сообщение каждые 4 секунды
    
    return () => clearInterval(messageInterval);
  }, [messages.length]);
  
  // Initialize layout before data is loaded
  useEffect(() => {
    // Set a timer to prevent flashing of loading state for fast connections
    const timer = setTimeout(() => {
      if (slideshowImages.length === 0) {
        // If slides haven't loaded yet, show loading state
        setLoading(true);
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [slideshowImages]);
  
  // Load slides from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch slides
        const slidesResponse = await axios.get('/api/slides');
        if (slidesResponse.data && slidesResponse.data.length > 0) {
          // Extract image URLs from slides
          const images = slidesResponse.data.map(slide => slide.image);
          setSlideshowImages(images);
        } else {
          // No slides available, set empty array
          setSlideshowImages([]);
        }
        // Set loading to false after slides are loaded
        setLoading(false);
      } catch (error) {
        console.error('Error fetching slides:', error);
        setLoading(false);
        setSlideshowImages([]);
      }
    };
    
    fetchData();
  }, []);
  
  // Fetch user coupons if user is logged in
  useEffect(() => {
    const fetchUserCoupons = async () => {
      if (!userInfo || !userInfo.token) return;
      
      try {
        setLoadingCoupons(true);
        console.log('Fetching user coupons...');
        const { data } = await axios.get('/api/coupons/my-coupons', {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        });
        
        console.log('Received coupons data:', data);
        
        // Правильно подсчитываем активные купоны в соответствии с MyCouponsPage
        if (Array.isArray(data)) {
          // Фильтруем только неиспользованные купоны
          const activeCount = data.filter(userCoupon => {
            // В MyCouponsPage используется прямая проверка userCoupon.isUsed
            return userCoupon && !userCoupon.isUsed;
          }).length;
          
          console.log('Active coupons count:', activeCount);
          console.log('Active coupons:', data.filter(c => c && !c.isUsed));
          setActiveCoupons(activeCount);
        } else {
          console.error('Received non-array data for coupons:', data);
          setActiveCoupons(0);
        }
      } catch (error) {
        console.error('Error fetching user coupons:', error);
        setActiveCoupons(0);
      } finally {
        setLoadingCoupons(false);
      }
    };
    
    fetchUserCoupons();
    
    // Добавляем интервал для периодического обновления данных о купонах
    const intervalId = setInterval(fetchUserCoupons, 10000); // Обновляем каждые 10 секунд для более быстрого обновления
    
    // Очищаем интервал при размонтировании компонента
    return () => clearInterval(intervalId);
  }, [userInfo]);
  
  // Handle click on gift icon to navigate to coupons page
  const handleCouponClick = () => {
    navigate('/my-coupons');
  };

  return (
    <SeasonalHero onThemeChange={handleThemeChange}>
      {/* SEO метаданные */}
      <SEO 
        title={PAGE_SEO.home.title}
        description={PAGE_SEO.home.description}
        keywords={PAGE_SEO.home.keywords}
        canonicalUrl={getCanonicalUrl(PAGE_SEO.home.path)}
      />
      
      {/* Структурированные данные для поисковых систем */}
      <StructuredData data={generateOrganizationSchema()} />
      <StructuredData data={generateLocalBusinessSchema()} />
      <Container 
        maxWidth="xl" 
        sx={{ 
          position: 'relative',
          // Увеличиваем отступ сверху на мобильных устройствах, чтобы не уходить под хэдер
          mt: { xs: '30px', sm: '30px', md: '20px' },  
          minHeight: { xs: 'auto', md: '70vh' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          pb: { xs: 2, sm: 2, md: 3 }, // Уменьшаем отступ снизу, так как панель с контактами будет отдельно
          overflow: 'visible',  // Чтобы логотип мог выходить за пределы контейнера
          zIndex: 1,  // Снижаем z-index контейнера
          px: { xs: 1, sm: 2, md: 3 } // Уменьшаем горизонтальные отступы на мобильных устройствах
        }}
      >
        {/* Иконка купонов для авторизованных пользователей */}
        {userInfo && (
          <Box 
            sx={{ 
              position: 'absolute',
              top: { xs: '10px', sm: '15px', md: '20px' },
              right: { xs: '10px', sm: '20px', md: '30px' },
              zIndex: 100,
              display: 'flex',
              alignItems: 'center',
              gap: { xs: '8px', sm: '10px', md: '12px' }
            }}
          >
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <IconButton 
                onClick={handleCouponClick}
                sx={{ 
                  backgroundColor: theme.palette.secondary.main,
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: theme.palette.secondary.dark,
                  },
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  width: { xs: '48px', sm: '56px', md: '64px' },
                  height: { xs: '48px', sm: '56px', md: '64px' },
                }}
              >
                <Badge 
                  badgeContent={activeCoupons} 
                  color="error"
                  max={99}
                  invisible={activeCoupons === 0}
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: { xs: '10px', sm: '12px', md: '14px' },
                      height: { xs: '18px', sm: '22px', md: '24px' },
                      minWidth: { xs: '18px', sm: '22px', md: '24px' },
                      fontWeight: 'bold',
                    }
                  }}
                >
                  <CardGiftcardIcon sx={{ fontSize: { xs: '24px', sm: '28px', md: '32px' } }} />
                </Badge>
              </IconButton>
            </motion.div>
          </Box>
        )}
        {/* Верхний ряд: логотип и слайды */}
        <Grid container spacing={{ xs: 0, md: 4 }} alignItems="center" justifyContent="center" sx={{ mb: { xs: 0, md: 2 } }}>
          {/* Объединенный компонент логотипа и слайдов */}
          <Grid item xs={12} md={12} lg={12} xl={12}>
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
            >
              <Box 
                sx={{ 
                  position: 'relative',
                  width: '100%',
                  maxWidth: { xs: '100%', sm: '90%', md: '80%', lg: '75%' },
                  height: { xs: '250px', sm: '350px', md: '450px', lg: '500px' }, // Уменьшаем высоту на мобильных устройствах
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: { xs: 3, md: 4 }, // Увеличиваем отступ снизу на мобильных устройствах
                  zIndex: 50,
                  mt: { xs: '20px', sm: '30px', md: '20px' }, // Уменьшаем отступ сверху на мобильных устройствах
                  px: { xs: 1, sm: 1, md: 2 }, // Добавляем небольшие отступы по бокам
                  mx: { xs: 1, sm: 2, md: 0 }, // Добавляем отступы по бокам на мобильных устройствах
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: { xs: '12px', md: '16px' }, // Уменьшаем скругление углов на мобильных устройствах
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                    zIndex: -1
                  }
                }}
              >
                {/* Компонент LogoSlideshow для чередования логотипа и слайдов */}
                <Paper 
                  elevation={8} 
                  sx={{ 
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden',
                    borderRadius: '16px',
                    position: 'relative',
                    backgroundColor: 'transparent',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(0, 0, 0, 0.1)',
                      zIndex: 2,
                      pointerEvents: 'none'
                    }
                  }}
                >
                  {loading ? (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center',
                      height: '100%'
                    }}>
                      <CircularProgress 
                        size={80} 
                        thickness={4} 
                        color="primary" 
                      />
                    </Box>
                  ) : (
                    <LogoSlideshow 
                      logoInterval={7000}
                      slideInterval={5000}
                      height="100%"
                      logoScale={{ xs: 1.6, sm: 1.5, md: 1.4, lg: 1.3 }}
                      transparentLogoBackground={true}
                    />
                  )}
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      bottom: 0, 
                      left: 0, 
                      right: 0, 
                      p: 2, 
                      background: 'linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0))',
                      zIndex: 3
                    }}
                  >
                    <motion.div
                      key={messages[currentMessageIndex]}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Typography variant="h5" sx={{ 
                        color: 'white', 
                        textShadow: '0 2px 4px rgba(0,0,0,0.7)',
                        fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.7rem', lg: '1.9rem' },
                        fontWeight: 600,
                        letterSpacing: '0.5px',
                        padding: '5px 10px',
                        textAlign: 'center'
                      }}>
                        {messages[currentMessageIndex]}
                      </Typography>
                    </motion.div>
                  </Box>
                </Paper>
              </Box>
            </motion.div>
          </Grid>
        </Grid>

        {/* Нижний ряд: текст и кнопки */}
        <Box sx={{ 
          textAlign: 'center',
          mt: { xs: 0, md: 2 },  // Уменьшаем отступ между логотипом и текстом
          position: 'relative',
          left: 0,
          right: 0,
          pb: { xs: 1, md: 5 },
          zIndex: 1  // Очень низкий z-index для текста и кнопок
        }}>
          {/* Заголовок */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            style={{ width: '100%' }}
          >
            <Typography 
              variant="h2" 
              component="h1" 
              sx={{ 
                fontWeight: 700, 
                mb: { xs: 1, md: 2 },
                fontSize: { xs: '1.7rem', sm: '2.2rem', md: '3rem', lg: '3.5rem' },
                textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                letterSpacing: '0.5px',
                lineHeight: 1.2,
                textAlign: 'center', // Выравнивание по центру
                width: '100%', // Полная ширина для выравнивания текста
                px: { xs: 1, sm: 2, md: 0 } // Отступы по бокам для мобильных
              }}
            >
              Аренда недвижимости в Тюмени
            </Typography>
          </motion.div>
          
          {/* Подзаголовок */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <Typography 
              variant="h5" 
              sx={{ 
                mb: { xs: 2, md: 4 },
                fontSize: { xs: '1rem', sm: '1.2rem', md: '1.5rem' },
                opacity: 0.9,
                textShadow: '0 1px 8px rgba(0,0,0,0.3)',
                px: { xs: 2, md: 0 }, // Добавляем боковые отступы на мобильных
                textAlign: 'center', // Выравнивание по центру
                width: '100%' // Полная ширина для выравнивания текста
              }}
            >
              Коттеджи, бани, дачи для отдыха с семьей и друзьями
            </Typography>
          </motion.div>
          
          {/* Кнопки */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            style={{ 
              display: 'flex', 
              flexDirection: isSmall ? 'column' : 'row', 
              justifyContent: 'center',
              gap: isSmall ? '15px' : '16px',
              margin: '0 auto',
              maxWidth: '600px',
              width: isSmall ? '100%' : 'auto',
              padding: isSmall ? '0 10px' : '0'
            }}
          >
            <Button 
              variant="contained" 
              color="primary" 
              size="large" 
              component={RouterLink} 
              to="/gallery"
              sx={{ 
                px: { xs: 3, md: 4 }, 
                py: { xs: 1.5, md: 1.5 },
                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                minWidth: { xs: '200px', sm: '200px' },
                width: isSmall ? '100%' : 'auto',
                boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
              }}
            >
              Смотреть галерею
            </Button>
            <Button 
              variant="outlined" 
              color="inherit" 
              size="large" 
              component={RouterLink} 
              to="/booking"
              sx={{ 
                px: { xs: 3, md: 4 }, 
                py: { xs: 1.5, md: 1.5 },
                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                minWidth: { xs: '200px', sm: '200px' },
                width: isSmall ? '100%' : 'auto',
                borderColor: 'white',
                borderWidth: '2px',
                '&:hover': {
                  borderColor: 'white',
                  borderWidth: '2px',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)'
                }
              }}
            >
              Забронировать
            </Button>
          </motion.div>
          
          {/* Убираем панель с контактами из hero-секции */}
          
          {/* Баннер с акциями перенесен на главную страницу */}
        </Box>
      </Container>
    </SeasonalHero>
  );
};

// Компоненты для секций будут определены ниже

// Featured Properties Section
const FeaturedProperties = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    // Проверяем, есть ли данные в настройках
    const checkSettingsForProperties = async () => {
      try {
        // Проверяем, есть ли объекты в настройках
        const { data: settings } = await axios.get('/api/settings');
        if (settings && settings.featuredProperties && Array.isArray(settings.featuredProperties)) {
          const processedData = settings.featuredProperties.map(item => ({
            ...item,
            _id: item._id || `prop-${Math.random().toString(36).substr(2, 9)}`,
            price: item.price || 0
          }));
          setProperties(processedData);
          setLoading(false);
          return true;
        }
        return false;
      } catch (error) {
        console.log('Settings do not contain properties, trying direct API');
        return false;
      }
    };
    
    const fetchProperties = async () => {
      // Сначала проверяем настройки
      const foundInSettings = await checkSettingsForProperties();
      if (foundInSettings) return;
      
      try {
        // Пытаемся получить данные из API
        const { data } = await axios.get('/api/properties');
        // Проверяем и обрабатываем данные
        if (Array.isArray(data)) {
          const processedData = data.map(item => ({
            ...item,
            _id: item._id || `prop-${Math.random().toString(36).substr(2, 9)}`,
            price: item.price || 0
          }));
          setProperties(processedData);
        } else {
          throw new Error('Data is not an array');
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
        // Если API недоступен, используем моковые данные
        const mockProperties = [
          {
            _id: 'prop-1',
            name: 'Коттедж "Сосновый"',
            description: 'Просторный коттедж с 4 спальнями, сауной и барбекю.',
            price: 15000,
            images: ['/assets/properties/property1.jpg']
          },
          {
            _id: 'prop-2',
            name: 'Баня "Берёзовая"',
            description: 'Традиционная русская баня с купелью и комнатой отдыха.',
            price: 8000,
            images: ['/assets/properties/property2.jpg']
          },
          {
            _id: 'prop-3',
            name: 'Дача "У озера"',
            description: 'Уютная дача на берегу озера с прекрасным видом.',
            price: 10000,
            images: ['/assets/properties/property3.jpg']
          }
        ];
        setProperties(mockProperties);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProperties();
  }, []);
  
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }
  
  // If no properties found, show placeholder message
  if (properties.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" sx={{ mb: 4, textAlign: 'center', fontWeight: 700 }}>
          Популярные объекты
        </Typography>
        <Alert severity="info">В данный момент нет доступных объектов недвижимости</Alert>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Typography variant="h3" component="h2" sx={{ mb: 4, textAlign: 'center', fontWeight: 700 }}>
        Популярные объекты
      </Typography>
      <Typography variant="h6" sx={{ mb: 6, textAlign: 'center', color: 'text.secondary' }}>
        Выберите идеальное место для вашего отдыха
      </Typography>
      
      <Grid container spacing={4}>
        {properties.slice(0, 3).map((property) => (
          <Grid item xs={12} sm={6} md={4} key={property._id}>
            <Card sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 12px 20px rgba(0,0,0,0.2)'
              }
            }}>
              <CardMedia
                component="img"
                height="240"
                image={property.mainImage || (property.PropertyImages && property.PropertyImages.length > 0 ? property.PropertyImages[0].imageUrl : '/assets/placeholder-property.jpg')}
                alt={property.name}
              />
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography gutterBottom variant="h5" component="h3">
                  {property.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {property.description && property.description.length > 100 
                    ? `${property.description.substring(0, 100)}...` 
                    : property.description}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                    от {property.rentalType === 'hourly' ? property.pricePerHour : property.pricePerDay || property.price} ₽
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    {property.rentalType === 'hourly' ? '/ час' : '/ сутки'}
                  </Typography>
                </Box>
              </CardContent>
              <CardActions>
                <Button size="small" color="primary" component={RouterLink} to={`/property/${property.id || property._id}`}>
                  Подробнее
                </Button>
                <Button size="small" color="primary" component={RouterLink} to={`/booking?property=${property.id || property._id}`}>
                  Забронировать
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
      

      <Box sx={{ textAlign: 'center', mt: 6 }}>
        <Button 
          variant="contained" 
          color="primary" 
          size="large"
          component={RouterLink}
          to="/gallery"
          sx={{ px: 4, py: 1.2 }}
        >
          Смотреть все объекты
        </Button>
      </Box>
    </Container>
  );
};

// Testimonials Section
// Компонент отзывов, который получает данные из родительского компонента
const Testimonials = ({ testimonialData, isLoading, errorMessage }) => {
  // Используем данные, переданные из родительского компонента
  const testimonials = testimonialData;
  const loading = isLoading;
  const error = errorMessage;
  
  // Проверка наличия данных без логирования
  
  if (loading) {
    return (
      <Box sx={{ py: 8, backgroundColor: 'background.default', textAlign: 'center' }}>
        <Container maxWidth="lg">
          <CircularProgress />
        </Container>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ py: 8, backgroundColor: 'background.default' }}>
        <Container maxWidth="lg">
          <Alert severity="error">{error}</Alert>
        </Container>
      </Box>
    );
  }
  
  // Подготовка к рендерингу компонента с отзывами
  
  // Format date function
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('ru-RU', options);
  };
  
  // Подготовка данных для рендеринга отзывов
  
  return (
    <Box sx={{ py: 8, backgroundColor: 'background.default' }}>
      <Container maxWidth="lg">
        <Typography variant="h3" component="h2" sx={{ mb: 4, textAlign: 'center', fontWeight: 700 }}>
          Отзывы клиентов
        </Typography>
        <Typography variant="h6" sx={{ mb: 6, textAlign: 'center', color: 'text.secondary' }}>
          Что говорят о нас наши клиенты
        </Typography>
        
        <Grid container spacing={4}>
          {testimonials && testimonials.length > 0 ? testimonials.slice(0, 3).map((testimonial) => {
            // Проверяем наличие идентификатора
            const testimonialId = testimonial._id || testimonial.id || `testimonial-${Math.random().toString(36).substr(2, 9)}`;
            
            return (
              <Grid item xs={12} md={4} key={testimonialId}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 4, 
                    height: '100%',
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" paragraph sx={{ fontStyle: 'italic', textAlign: 'justify' }}>
                    {testimonial.text}
                  </Typography>
                  
                  {(testimonial.propertyName || testimonial.property?.name) && (
                    <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                      Объект: {testimonial.propertyName || testimonial.property?.name}
                    </Typography>
                  )}
                  
                  <Rating 
                    value={testimonial.rating || 5} 
                    readOnly 
                    size="small" 
                    sx={{ mt: 1 }}
                  />
                </Box>
                <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center' }}>
                  <Avatar
                    src={testimonial.avatar || '/assets/avatars/default-avatar.jpg'}
                    alt={testimonial.name || testimonial.user?.name || 'Пользователь'}
                    sx={{ width: 50, height: 50, mr: 2 }}
                  >
                    {(testimonial.name || testimonial.user?.name) ? (testimonial.name || testimonial.user?.name).charAt(0) : '?'}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {testimonial.name || testimonial.user?.name || 'Пользователь'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(testimonial.date || testimonial.createdAt || new Date())}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          );
          }) : (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  В данный момент нет отзывов
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
        
        <Box sx={{ textAlign: 'center', mt: 6, display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button 
            variant="outlined" 
            color="primary" 
            size="large"
            component={RouterLink}
            to="/testimonials"
            sx={{ px: 4, py: 1.2 }}
          >
            Оставить отзыв
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            size="large"
            component={RouterLink}
            to="/testimonials"
            sx={{ px: 4, py: 1.2 }}
          >
            Все отзывы
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

// Recent Blog Posts Section
const RecentBlogPosts = ({ posts, isLoading, errorMessage }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  if (isLoading) {
    return (
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" gutterBottom align="center">
            Недавние статьи
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }
  
  if (errorMessage) {
    return (
      <Box sx={{ py: 8, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" gutterBottom align="center">
            Недавние статьи
          </Typography>
          <Alert severity="error" sx={{ mt: 2 }}>{errorMessage}</Alert>
        </Container>
      </Box>
    );
  }
  
  if (!posts || posts.length === 0) {
    return null; // Не показываем раздел, если нет статей
  }
  
  return (
    <Box sx={{ py: 8, bgcolor: 'background.default' }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" component="h2">
            Недавние статьи
          </Typography>
          <Button 
            component={RouterLink} 
            to="/blog"
            variant="outlined" 
            color="primary"
            endIcon={<ArrowForwardIcon />}
          >
            Все статьи
          </Button>
        </Box>
        
        <Grid container spacing={3}>
          {posts.map((post) => {
            const formattedDate = post.createdAt ? 
              format(new Date(post.createdAt), 'd MMMM yyyy', { locale: ru }) : 
              'Нет даты';
              
            return (
              <Grid item xs={12} sm={6} md={4} key={post.id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                    },
                    textDecoration: 'none'
                  }}
                  component={RouterLink}
                  to={`/blog/${post.slug || post.id}`}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={post.featuredImage || '/assets/placeholder-blog.jpg'}
                    alt={post.title}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="h3" gutterBottom noWrap>
                      {post.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        height: '4.5em',
                        textAlign: 'justify' // Добавляем выравнивание по ширине
                      }}
                    >
                      {post.excerpt || post.content?.substring(0, 120) + '...'}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                        <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {formattedDate}
                        </Typography>
                      </Box>
                      {post.views !== undefined && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <VisibilityIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {post.views}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

// About Us Section
const AboutUs = () => {
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  
  useEffect(() => {
    const fetchAboutData = async () => {
      try {
        const { data } = await axios.get('/api/settings');
        
        // Преобразуем данные из общих настроек в формат для секции "О нас"
        if (data) {
          setAboutData({
            title: data.aboutTitle || 'О нас',
            description: data.aboutDescription || 'DomikTyumen - это сервис аренды загородной недвижимости в Тюмени и Тюменской области.',
            guarantees: data.aboutGuarantees,
            image: data.aboutImage || '/assets/about-us.jpg',
            buttonText: 'Узнать больше'
          });
        }
      } catch (error) {
        console.error('Error fetching about data:', error);
        // Если API не работает, используем дефолтные данные
        setAboutData({
          title: 'О нас',
          description: 'DomikTyumen - это сервис аренды загородной недвижимости в Тюмени и Тюменской области. Мы предлагаем комфортабельные коттеджи, традиционные русские бани и уютные дачи для отдыха с семьей и друзьями.',
          guarantees: 'Наша миссия - сделать ваш отдых незабываемым. Все наши объекты тщательно отобраны и поддерживаются в отличном состоянии. Мы гарантируем чистоту, комфорт и безопасность на всех наших объектах.',
          image: '/assets/about-us.jpg',
          buttonText: 'Узнать больше'
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchAboutData();
  }, []);
  
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }
  
  if (!aboutData) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error">Не удалось загрузить информацию</Alert>
      </Container>
    );
  }
  
  return (
    <Box sx={{ 
      py: 8, 
      bgcolor: 'background.default',
      borderRadius: '16px',
      my: 4
    }}>
      <Container maxWidth="lg">
        <Box sx={{ 
          textAlign: 'center', 
          mb: 5 
        }}>
          <Typography 
            variant="h3" 
            component="h2" 
            sx={{ 
              mb: 2, 
              fontWeight: 700,
              color: 'white',
              position: 'relative',
              display: 'inline-block',
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -10,
                left: '50%',
                width: '80px',
                height: '4px',
                backgroundColor: 'primary.main',
                transform: 'translateX(-50%)'
              }
            }}
          >
            {aboutData.title || 'О нас'}
          </Typography>
        </Box>
        
        <Paper
          elevation={4}
          sx={{
            borderRadius: '16px',
            overflow: 'hidden',
            backgroundColor: theme => theme.palette.mode === 'dark' 
              ? 'rgba(30, 30, 30, 0.95)' 
              : 'rgba(255, 255, 255, 0.95)',
            boxShadow: theme => theme.palette.mode === 'dark'
              ? '0 15px 35px rgba(0, 0, 0, 0.4)'
              : '0 15px 35px rgba(0, 0, 0, 0.25)',
            border: theme => theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(255, 255, 255, 0.3)',
            height: '100%'
          }}
        >
          <Grid container spacing={0} sx={{ height: '100%' }}>
            <Grid item xs={12} md={6}>
              <Box 
                sx={{ 
                  p: 4, 
                  height: '100%',
                  minHeight: { xs: 'auto', md: '450px' },
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  borderRight: { xs: 'none', md: '1px solid rgba(0, 0, 0, 0.08)' },
                  borderBottom: { xs: '1px solid rgba(0, 0, 0, 0.08)', md: 'none' }
                }}
              >
                <Box>
                  <Typography 
                    variant="body1" 
                    paragraph 
                    sx={{ 
                      fontSize: '1.1rem', 
                      lineHeight: 1.7,
                      mb: 3,
                      color: theme => theme.palette.mode === 'dark' ? '#e0e0e0' : '#333333',
                      textAlign: 'justify' // Выравнивание по ширине
                    }}
                  >
                    {aboutData.description || 'Информация о компании'}
                  </Typography>
                  
                  {aboutData.guarantees && (
                    <Box sx={{ 
                      mt: 2, 
                      p: 3, 
                      borderLeft: '4px solid',
                      borderColor: '#3498db',
                      backgroundColor: theme => theme.palette.mode === 'dark' 
                        ? 'rgba(52, 152, 219, 0.15)' 
                        : 'rgba(52, 152, 219, 0.05)',
                      borderRadius: '0 8px 8px 0',
                      boxShadow: theme => theme.palette.mode === 'dark'
                        ? '0 4px 15px rgba(0, 0, 0, 0.2)'
                        : '0 4px 15px rgba(0, 0, 0, 0.05)'
                    }}>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontStyle: 'italic',
                          color: theme => theme.palette.mode === 'dark' ? '#b0c4de' : '#2c3e50',
                          lineHeight: 1.6,
                          position: 'relative',
                          textAlign: 'justify', // Выравнивание по ширине
                          '&::before': {
                            content: '"\u201C"',
                            fontFamily: 'Georgia, serif',
                            fontSize: '2.5rem',
                            position: 'absolute',
                            left: -10,
                            top: -20,
                            opacity: theme => theme.palette.mode === 'dark' ? 0.5 : 0.3,
                            color: '#3498db'
                          }
                        }}
                      >
                        {aboutData.guarantees}
                      </Typography>
                    </Box>
                  )}
                </Box>
                
                <Button 
                  variant="contained" 
                  color="primary"
                  component={RouterLink}
                  to="/about"
                  sx={{ 
                    mt: 4,
                    alignSelf: 'flex-start',
                    px: 3,
                    py: 1.2,
                    borderRadius: '8px',
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  {aboutData.buttonText || 'Узнать больше'}
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box 
                sx={{ 
                  p: 4, 
                  height: '100%',
                  minHeight: { xs: 'auto', md: '450px' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}
              >
                <Box sx={{ 
                  textAlign: 'center',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <Box 
                    component="img"
                    src="/assets/o-nas.png"
                    alt="О нас"
                    sx={{ 
                      width: '100%',
                      maxWidth: '300px',
                      height: 'auto',
                      mb: 3,
                      borderRadius: '8px',
                      boxShadow: '0 5px 20px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Box sx={{ 
                    bgcolor: '#2c3e50 !important', // Темно-синий фон для панели с названием компании
                    color: 'white',
                    p: 3,
                    borderRadius: '8px',
                    width: '100%',
                    textAlign: 'center',
                    boxShadow: theme => theme.palette.mode === 'dark'
                      ? '0 5px 15px rgba(0,0,0,0.4)'
                      : '0 5px 15px rgba(0,0,0,0.2)'
                  }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      DomikTyumen
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, opacity: 0.9 }}>
                      Комфортный отдых в Тюмени
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

// Компонент панели с контактами (вынесен из Hero)
const ContactPanel = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        py: { xs: 2, sm: 2, md: 3 },
        mt: { xs: 0, sm: 0, md: 0 },
        mb: { xs: 3, sm: 3, md: 4 },
        position: 'relative',
        zIndex: 10
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        style={{ 
          position: 'relative',
          zIndex: 1000 // Максимально увеличиваем z-index для лучшей видимости
        }}
      >
        <Paper
          elevation={3}
          sx={{
            mx: 'auto',
            maxWidth: { xs: '95%', sm: '90%', md: '80%', lg: '70%' },
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 2, sm: 2, md: 2 },
            position: 'relative',
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(30, 30, 30, 0.9)' 
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 20px rgba(0, 0, 0, 0.3)'
              : '0 4px 20px rgba(0, 0, 0, 0.15)',
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 1 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700,
                mb: 2,
                color: theme.palette.text.primary
              }}
            >
              Связаться с нами
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 2,
                color: theme.palette.text.secondary,
                maxWidth: '600px',
                mx: 'auto'
              }}
            >
              Выберите удобный способ связи для бронирования или получения дополнительной информации
            </Typography>
          </Box>
          <ContactButtons spacing={isSmall ? 5 : 8} />
        </Paper>
      </motion.div>
    </Container>
  );
};

// HomePage component that includes Hero and other sections
const HomePage = () => {
  const { currentLayout } = useThemeLayout();
  const SelectedLayout = layoutMap[currentLayout] || layoutMap.classic;
  
  // Состояние для отзывов
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialLoading, setTestimonialLoading] = useState(true);
  const [testimonialError, setTestimonialError] = useState(null);
  
  // Загрузка отзывов
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get('/api/testimonials/featured');
        const data = response.data;
        
        if (Array.isArray(data) && data.length > 0) {
          setTestimonials(data);
        } else {
          // Если нет избранных, пробуем загрузить обычные отзывы
          const regularResponse = await axios.get('/api/testimonials');
          const regularData = regularResponse.data;
          
          if (Array.isArray(regularData) && regularData.length > 0) {
            setTestimonials(regularData);
          }
        }
      } catch (error) {
        setTestimonialError('Не удалось загрузить отзывы');
      } finally {
        setTestimonialLoading(false);
      }
    };
    
    fetchTestimonials();
  }, []);
  
  // Состояние для недавних статей блога
  const [recentPosts, setRecentPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [postsError, setPostsError] = useState(null);
  
  // Загрузка недавних статей блога
  useEffect(() => {
    const fetchRecentPosts = async () => {
      try {
        const response = await axios.get('/api/blog', {
          params: {
            page: 1,
            limit: 3,
            status: 'published'
          }
        });
        
        if (response.data && response.data.blogPosts) {
          setRecentPosts(response.data.blogPosts);
        }
      } catch (error) {
        setPostsError('Не удалось загрузить недавние статьи');
      } finally {
        setPostsLoading(false);
      }
    };
    
    fetchRecentPosts();
  }, []);

  // Define sections for the layout
  const sections = [
    { type: 'featured', component: FeaturedProperties },
    { type: 'services', component: FeaturedServices },
    { type: 'tips', component: FeaturedTips },
    { 
      type: 'recentPosts', 
      component: () => <RecentBlogPosts 
        posts={recentPosts} 
        isLoading={postsLoading} 
        errorMessage={postsError} 
      /> 
    },
    { 
      type: 'testimonials', 
      component: () => <Testimonials 
        testimonialData={testimonials} 
        isLoading={testimonialLoading} 
        errorMessage={testimonialError} 
      /> 
    },
    { type: 'about', component: AboutUs },
  ];
  
  const handleVerificationSuccess = () => {
    setShowVerification(false);
  };
  
  return (
    <Box>
      <Hero />
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 2, mt: 3, mb: 3 }}>
        <PromotionBanner />
      </Box>
      <TelegramBotBanner />
      <ContactPanel />
      <SelectedLayout sections={sections} />
    </Box>
  );
};

export default HomePage;
